import {useState} from "react";
import {Link, useParams} from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import { PageTitle } from "@/components/ui/headings";
import {Button} from "@/components/ui/button.tsx";
import { ProfilePageSkeleton } from "@/components/ProfilePageSkeleton.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import {Mentee} from "@/types/Mentee.ts";
import {UserRole} from "@/types/UserRole.ts";
import {ProfileLeftPanel} from "@/components/ProfileLeftPanel.tsx";
import {getMenteeById, getMenteeByIdWithAllMentorChoices, getMenteeSessions} from "@/services/menteeService.ts";
import {Pencil} from "lucide-react";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {PreferenceDetails} from "@/components/PreferenceDetails.tsx";
import {useQuery} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getItemsOfAllTypes} from "@/services/itemService.ts";
import {useTranslation} from "react-i18next";
import {getLocalizedFieldName} from "@/utils/translation-helper.ts";
import {Suggestions} from "@/pages/dashboard/mentee/suggestions";
import {fetchLastDateToSubmitPreferences} from "@/services/settingsService.ts";
import ExpandableText from "@/components/ExpandableText.tsx";
import {getDate, isPreferenceSubmissionPeriod, sortBySessionDateAsc, formatSessionTime12h} from "@/utils/time-helper.ts";

import {Mail, ArrowRight} from "lucide-react";
import MentorInfoCard from "@/components/MentorInfoCard.tsx";
import {getImgSrc} from "@/utils/file-upload.ts";

const MenteeProfilePage = () => {
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const {t} = useTranslation();
    const {id} = useParams();
    const {loggedInUser} = useAuth();
    const isAdmin = (loggedInUser && loggedInUser.role === UserRole.ADMIN) || false;

    const {data: mentee} = useQuery({
        queryKey: queryKeys.fetchMentees.one,
        queryFn: () => getMenteeById(id ? parseInt(id) : -1),
        initialData: null,
    });

    const getLocalizedValue = (key: string) => mentee ? mentee[getLocalizedFieldName(key) as keyof Mentee] as string : key;

    const {data: submitPreferenceDeadline} = useQuery({
        queryKey: queryKeys.fetchSettings.lastDateToSubmitPreferences,
        queryFn: () => fetchLastDateToSubmitPreferences(),
    });

    const {data: criteria} = useQuery({
        queryKey: queryKeys.fetchCriteria.all,
        queryFn: () => getItemsOfAllTypes(),
    });

    const {data: menteeWithSuggestedMentors} = useQuery({
        queryKey: queryKeys.fetchSuggestions.allChoicesByMenteeId,
        queryFn: () => getMenteeByIdWithAllMentorChoices(mentee ? mentee.id : -1),
        enabled: isAdmin && mentee != null && !isPreferenceSubmissionPeriod(submitPreferenceDeadline),
    });

    const {data: menteeSessions} = useQuery({
        queryKey: queryKeys.fetchMentees.sessions(mentee?.id ?? -1),
        queryFn: () => getMenteeSessions(mentee!.id),
        enabled: mentee != null && mentee.mentor != null,
    });

    return (
        <>
            {mentee ? (
                <div className="container pt-8 pb-12">
                    <div className="mx-auto max-w-4xl">
                        <header className="mb-8">
                            <PageTitle>{t("profile.menteeProfile")}</PageTitle>
                            <p className="mt-1 text-sm text-text-2">
                                {t("profile.menteeProfileDesc")}
                            </p>
                        </header>
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr] lg:items-start">
                            <aside className="min-w-0 space-y-4">
                                <ProfileLeftPanel user={{...mentee.user, role: UserRole.MENTEE}}/>
                            </aside>
                            <div className="min-w-0 space-y-6">
                            <Card className="border border-border bg-bg-card shadow-sm">
                                <CardHeader className="border-b border-border">
                                    <div className="flex items-center justify-between gap-4">
                                        <CardTitle className="text-lg font-semibold text-text-1">
                                            {t("profile.generalInformation")}
                                        </CardTitle>
                                        {loggedInUser && (isAdmin || (loggedInUser?.mentee?.id ?? -1) === mentee.id) && (
                                            <a 
                                                href={`/mentees/${mentee.id}/edit/`} 
                                                className="text-text-2 hover:text-springGreen transition-colors p-2 -mr-2"
                                                aria-label={t("common.edit")}
                                            >
                                                <Pencil className="h-5 w-5"/>
                                            </a>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    <dl className="divide-y divide-border">
                                        {mentee.studentStatus &&
                                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-6">
                                                <dt className="text-sm font-semibold text-text-1 mb-1 sm:mb-0">
                                                    {t("mentee.studentStatus")}
                                                </dt>
                                                <dd className="text-sm text-text-2 sm:col-span-2">
                                                    {getLocalizedValue("studentStatus")}
                                                </dd>
                                            </div>
                                        }
                                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-6">
                                            <dt className="text-sm font-semibold text-text-1 mb-1 sm:mb-0">
                                                {t("criteria.discipline")}
                                            </dt>
                                            <dd className="text-sm text-text-2 sm:col-span-2">
                                                {getLocalizedValue("discipline")}
                                            </dd>
                                        </div>
                                        {mentee.supervisor &&
                                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-6">
                                                <dt className="text-sm font-semibold text-text-1 mb-1 sm:mb-0">
                                                    {t("mentee.supervisor")}
                                                </dt>
                                                <dd className="text-sm text-text-2 sm:col-span-2">
                                                    {mentee.supervisor}
                                                </dd>
                                            </div>
                                        }
                                        {mentee.institution &&
                                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-6">
                                                <dt className="text-sm font-semibold text-text-1 mb-1 sm:mb-0">
                                                    {t("mentee.institution")}
                                                </dt>
                                                <dd className="text-sm text-text-2 sm:col-span-2">
                                                    {getLocalizedValue("institution")}
                                                </dd>
                                            </div>
                                        }
                                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-6">
                                            <dt className="text-sm font-semibold text-text-1 mb-1 sm:mb-0">
                                                {t("mentor.region")}
                                            </dt>
                                            <dd className="text-sm text-text-2 sm:col-span-2">
                                                {getLocalizedValue("region")}
                                            </dd>
                                        </div>
                                        {loggedInUser && (loggedInUser.role === UserRole.ADMIN || loggedInUser.role === UserRole.MENTOR || (loggedInUser.mentee?.id ?? -1) === mentee.id) &&
                                            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-6">
                                                <dt className="text-sm font-semibold text-text-1 mb-1 sm:mb-0">
                                                    {t("common.phoneNumber")}
                                                </dt>
                                                <dd className="text-sm text-text-2 sm:col-span-2">
                                                    {mentee.phoneNumber}
                                                </dd>
                                            </div>
                                        }
                                        <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-6">
                                            <dt className="text-sm font-semibold text-text-1 mb-1 sm:mb-0">
                                                {t("mentor.bio")}
                                            </dt>
                                            <dd className="text-sm text-text-2 sm:col-span-2">
                                                <ExpandableText text={getLocalizedValue("bio")} maxLength={300}
                                                                classNames="mt-0"/>
                                            </dd>
                                        </div>
                                    </dl>
                                </CardContent>
                            </Card>

                            <Card className="border border-border bg-bg-card shadow-sm">
                                <CardHeader className="border-b border-border">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                        <CardTitle className="text-lg font-semibold text-text-1">
                                            {t("common.mentor", "Mentor")}
                                        </CardTitle>
                                        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                                            {mentee.mentor?.user?.email && (isAdmin || (loggedInUser && (loggedInUser.mentee?.id ?? -1) === mentee.id)) && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-9 w-full gap-2 rounded-md bg-bg-card border-border hover:bg-bg-elevated sm:w-auto"
                                                    onClick={() => { window.location.href = `mailto:${mentee.mentor!.user.email}`; }}
                                                >
                                                    <Mail className="h-4 w-4" />
                                                    {t("mentee.emailMyMentor")}
                                                </Button>
                                            )}
                                            {isAdmin && !isPreferenceSubmissionPeriod(submitPreferenceDeadline) && (
                                                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                                                    <DialogTrigger asChild>
                                                        <Button className="h-9 w-full rounded-md bg-text-1 px-4 text-bg-card hover:opacity-95 sm:w-auto">
                                                            {mentee.mentor ? t("mentee.changeMentor") : t("mentee.assignMentor")}
                                                        </Button>
                                                    </DialogTrigger>
                                                    <DialogContent
                                                        className="overflow-y-scroll max-h-[600px] sm:min-w-[616px] md:min-w-[744px] lg:min-w-[1000px]">
                                                        <DialogHeader>
                                                            <DialogTitle>{t("mentee.assignMentor")}</DialogTitle>
                                                            <DialogDescription>
                                                                {t("mentee.assignMentorFromList")}
                                                            </DialogDescription>
                                                        </DialogHeader>
                                                        <div className="grid gap-4 py-4">
                                                            {!menteeWithSuggestedMentors ? (
                                                                <div className="flex flex-col space-y-3 ml-6">
                                                                    <div className="space-y-2">
                                                                        <Skeleton className="h-8 w-full"/>
                                                                        <Skeleton className="h-8 w-full"/>
                                                                        <Skeleton className="h-8 w-full"/>
                                                                        <Skeleton className="h-8 w-full"/>
                                                                        <Skeleton className="h-8 w-full"/>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <Suggestions
                                                                    menteeWithSuggestedMentors={menteeWithSuggestedMentors}
                                                                    isAdmin={isAdmin}
                                                                    closeDialog={() => setIsDialogOpen(false)}
                                                                />
                                                            )}
                                                        </div>
                                                        <DialogFooter>
                                                            <Button
                                                                variant="outline"
                                                                onClick={() => setIsDialogOpen(false)}
                                                            >
                                                                {t("common.close")}
                                                            </Button>
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </Dialog>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    {mentee.mentor ? (
                                        <MentorInfoCard
                                            mentorId={mentee.mentor.id}
                                            fullName={
                                                mentee.mentor.user?.fullName ??
                                                (mentee.mentor.user?.firstName && mentee.mentor.user?.lastName
                                                    ? `${mentee.mentor.user.firstName} ${mentee.mentor.user.lastName}`.trim()
                                                    : mentee.mentor.user?.firstName ?? mentee.mentor.user?.lastName ?? "Mentor")
                                            }
                                            profileSrc={getImgSrc(mentee.mentor.user?.profilePictureSrc ?? undefined)}
                                            title={mentee.mentor[getLocalizedFieldName("title") as keyof typeof mentee.mentor] as string}
                                            university={mentee.mentor[getLocalizedFieldName("university") as keyof typeof mentee.mentor] as string}
                                            careerFocus={mentee.mentor.careerFocus?.[getLocalizedFieldName("name") as keyof typeof mentee.mentor.careerFocus] as string | undefined}
                                            wrapInCard={false}
                                            className="p-6 sm:p-8"
                                        />
                                    ) : (
                                        <p className="text-sm text-text-2">
                                            {t("mentee.noMentor")}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Sessions card */}
                            {mentee.mentor && (
                                <Card className="border border-border bg-bg-card shadow-sm">
                                    <CardHeader className="border-b border-border">
                                        <div className="flex items-center justify-between gap-4">
                                            <CardTitle className="text-lg font-semibold text-text-1">
                                                {t("mentee.sessions")}
                                            </CardTitle>
                                            <Link
                                                to={`/mentors/${mentee.mentor.id}/mentees`}
                                                className="text-sm text-text-2 hover:text-springGreen transition-colors flex items-center gap-1.5 underline-offset-4 hover:underline"
                                            >
                                                {t("mentor.mentorshipGroupPageTitle", "Mentorship Group")}
                                                <ArrowRight className="h-4 w-4"/>
                                            </Link>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="pt-6">
                                        {menteeSessions ? (
                                            <div className="grid gap-4 sm:grid-cols-3">
                                                <div className="rounded-lg border border-border bg-bg-elevated/60 px-4 py-4">
                                                    <p className="text-xs font-semibold text-text-2 uppercase tracking-wide mb-3">{t("mentee.sessionsAttended")}</p>
                                                    {menteeSessions.attended.length === 0 ? (
                                                        <p className="text-sm text-text-3">
                                                            {t("mentee.sessionsNone")}
                                                        </p>
                                                    ) : (
                                                        <ul className="text-sm space-y-2 text-text-2">
                                                            {[...menteeSessions.attended].sort(sortBySessionDateAsc).map((s) => (
                                                                <li key={s.sessionId}>
                                                                    <Link
                                                                        to={`/session-attendance?sessionId=${s.sessionId}`}
                                                                        className="hover:text-springGreen underline-offset-4 hover:underline transition-colors"
                                                                    >
                                                                        {getDate(new Date(s.sessionDate + "T12:00:00"))} · {formatSessionTime12h(s.sessionTime)}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                                <div className="rounded-lg border border-border bg-bg-elevated/60 px-4 py-4">
                                                    <p className="text-xs font-semibold text-text-2 uppercase tracking-wide mb-3">{t("mentee.sessionsMissed")}</p>
                                                    {menteeSessions.missed.length === 0 ? (
                                                        <p className="text-sm text-text-3">
                                                            {t("mentee.sessionsNone")}
                                                        </p>
                                                    ) : (
                                                        <ul className="text-sm space-y-2 text-text-2">
                                                            {[...menteeSessions.missed].sort(sortBySessionDateAsc).map((s) => (
                                                                <li key={s.sessionId}>
                                                                    <Link
                                                                        to={`/session-attendance?sessionId=${s.sessionId}`}
                                                                        className="hover:text-springGreen underline-offset-4 hover:underline transition-colors"
                                                                    >
                                                                        {getDate(new Date(s.sessionDate + "T12:00:00"))} · {formatSessionTime12h(s.sessionTime)}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                                <div className="rounded-lg border border-border bg-bg-elevated/60 px-4 py-4">
                                                    <p className="text-xs font-semibold text-text-2 uppercase tracking-wide mb-3">{t("mentee.sessionsUpcoming")}</p>
                                                    {menteeSessions.upcoming.length === 0 ? (
                                                        <p className="text-sm text-text-3">
                                                            {t("mentee.sessionsNone")}
                                                        </p>
                                                    ) : (
                                                        <ul className="text-sm space-y-2 text-text-2">
                                                            {[...menteeSessions.upcoming].sort(sortBySessionDateAsc).map((s) => (
                                                                <li key={s.sessionId}>
                                                                    {getDate(new Date(s.sessionDate + "T12:00:00"))} · {formatSessionTime12h(s.sessionTime)}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col space-y-2">
                                                <Skeleton className="h-24 w-full"/>
                                                <Skeleton className="h-24 w-full"/>
                                                <Skeleton className="h-24 w-full"/>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Saved preference */}
                            {mentee.menteePreference && (
                                <PreferenceDetails
                                    criteria={criteria}
                                    preference={{mentee, ...mentee.menteePreference}}
                                    showActions={false}
                                />
                            )}
                        </div>
                        </div>
                    </div>
                </div>
            ) : (
                <ProfilePageSkeleton />
            )}
        </>
    )
}

export default MenteeProfilePage;
